import {
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  Container,
  Divider,
  Fade,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Slide,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { FC, MouseEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import Cover from "../../../../Components/Misc/Cover";
import Tabbar from "../../../../Components/Tabbar";

import { useLocation, useNavigate } from "react-router";
import { routePaths } from "../../../../Router/router";

import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import PosterImage from "../../../../Components/Misc/PosterImage";
import { ModalContext } from "../../../../Components/Modals";
import { assetService, cacheService, thumbnailService } from "../../../../Core/Api/services";
import { GetMetadataByTagName, IsPermissionGranted } from "../../../../Core/Utils/asset-object-utils";
import GlobalConst from "../../../../Core/Utils/Constants/globals";

// Redux
import {
  FolderZipOutlined,
  Inventory2Outlined,
  LockOutlined,
  MoreHoriz,
  SaveAltOutlined,
  VerifiedOutlined,
  Warning,
} from "@mui/icons-material";
import moment from "moment";
import { useSelector } from "react-redux";
import Viewer from "react-viewer";
import AssetVideoResolutionChip from "../../../../Components/Misc/Chips/AssetChip/asset-video-resolution-chip";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import ModalCreateShare from "../../../../Components/Modals/ModalCreateShare/modal-create-share";
import ModalEditMetadata, {
  ModalEditMetadataElementProps,
} from "../../../../Components/Modals/ModalEditMetadata/modal-edit-metadata";
import { ThumbnailSize } from "../../../../Core/Api/Thumbnail/thumbnail-api-service";
import { AclAssetAccessLevel } from "../../../../Core/Api/Types/acl-asset-access-level";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import { DbAssetObjectInfos } from "../../../../Core/Api/Types/db-asset-object-infos";
import { DbMetadataFieldInfos } from "../../../../Core/Api/Types/db-metadata-field-infos";
import { Licences } from "../../../../Core/Api/Types/licences";
import { PermissionType } from "../../../../Core/Api/Types/permission-type";
import {
  GetObject,
  GetTitleActiveRightData,
  GetTitleContentData,
  IsObjectLoading,
} from "../../../../Redux/Reducers/redux-slice-asset";
import { useAppSelector } from "../../../../Redux/redux-store";
import { AclMatch, GetTitleRouterPath, UserHasAcl, UserHasAcls, UserHasLicence } from "../../../../Utils/helpers";
import useBasketActions from "../../../../Utils/Hooks/useBasketActions";
import useCollectionActions from "../../../../Utils/Hooks/useCollectionActions";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";

interface ITitleHeadingProps {
  titleId: number;
  collapsed?: boolean;
}

const TitleHeading: React.FC<ITitleHeadingProps> = (props: ITitleHeadingProps) => {
  const { t } = useTranslation();
  const { titleId, collapsed = false } = props;
  const theme = useTheme();
  const isLoading = useAppSelector(IsObjectLoading);
  const title = useSelector(GetObject);
  const navigate = useNavigate();
  const location = useLocation();
  const headingContainer = useRef<HTMLDivElement>(null);
  const customerExtIdName = cacheService.GetCustomerExternalIdLabel();
  const metadata: DbMetadataFieldInfos[] = cacheService.GetMetadata();
  const titleContentData = useAppSelector(GetTitleContentData);
  const titleActiveRightData = useAppSelector(GetTitleActiveRightData);
  const productionDate = useMemo(
    () => GetMetadataByTagName(title!, "productionDate", metadata)?.value,
    [title, metadata]
  );
  const isDriveTitlePage = location.pathname.includes("drive");

  const [posterViewerVisible, setPosterViewerVisible] = useState<boolean>(false);

  const [hasCollapsed, setHasCollapsed] = useState<boolean>(false);
  const [shouldCollapse, setShouldCollapse] = useState<boolean>(isDriveTitlePage);
  const [collapsedHeadHover, setCollapsedHeadHover] = useState<boolean>(false);

  useEffect(() => {
    // we record first collapse
    if (!hasCollapsed && collapsed) {
      setShouldCollapse(true);
      setHasCollapsed(true);
    }
  }, [collapsed]);

  const handleCollapsedHeadClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget || !shouldCollapse) return;
    setShouldCollapse(false);
    setCollapsedHeadHover(false);
    setHasCollapsed(false);
  };

  if (!title && !isLoading) return <></>;

  return (
    <Box
      borderRadius={2}
      position="relative"
      borderColor="backgroundAlpha.border"
      sx={{
        background: theme.palette.background.default,
        borderRadius: 2,
        mx: 1,
        flexShrink: 0,
        mt: 1,
        pl: 2,
        pr: 3.5,
        boxSizing: "content-box",
        transition: theme.transitions.create(["background", "padding"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.shorter,
        }),
        ...theme.helpers(theme).panelHighlights,
        ...(collapsedHeadHover && {
          pt: 1,
          background: theme.palette.background[200],
        }),
      }}
    >
      <Cover
        sx={{ borderRadius: 2, position: "fixed" }}
        img={{ src: `/static/images/admin-title-cover-${theme.palette.mode}.png` }}
        objectPosition="top"
      />
      {isLoading ? (
        <TitleHeadingSkeleton />
      ) : (
        <Fade in={!isLoading} timeout={1000}>
          <Box>
            <Collapse in={!shouldCollapse} collapsedSize={GlobalConst.HEADING_HEIGHT_COLLAPSED}>
              <Fade in={!shouldCollapse}>
                <Stack gap={1} sx={{ opacity: collapsed ? 0 : 1 }}>
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={2}
                    zIndex={1}
                    pt={3}
                    pb={1}
                  >
                    <Stack direction="row" spacing={3} flexBasis="50%">
                      <Viewer
                        visible={posterViewerVisible}
                        noNavbar={true}
                        zIndex={2001}
                        onClose={() => {
                          setPosterViewerVisible(false);
                        }}
                        images={[
                          {
                            src: thumbnailService.GetObjectPosterUrl(titleId, ThumbnailSize.original),
                            alt: "Image viewer",
                          },
                        ]}
                      />
                      <PosterImage
                        width={125}
                        img={{
                          src: thumbnailService.GetObjectPosterUrl(titleId, ThumbnailSize.small),
                        }}
                        onClick={() => setPosterViewerVisible(true)}
                        sx={{ cursor: "pointer", flexShrink: 0 }}
                      />
                      <Stack alignItems="flex-start">
                        {isLoading ? (
                          <Typography component="h2" variant="h6" color="text.tier">
                            {GlobalConst.LOADING_TEXT}
                          </Typography>
                        ) : (
                          <Stack gap={2} justifyContent={"space-between"} flex={1}>
                            <Stack gap={2}>
                              <Stack gap={0.5}>
                                <Stack direction="row" gap={1} alignItems="center">
                                  <Typography component="h2" variant="body1" fontWeight={500} color="text.secondary">
                                    {title?.titleCustomerId && customerExtIdName
                                      ? `${customerExtIdName} #${title.titleCustomerId}`
                                      : `Title #${titleId}`}
                                  </Typography>

                                  <Stack direction="row" gap={0.5}>
                                    {titleActiveRightData &&
                                      titleActiveRightData.show &&
                                      titleActiveRightData.active && (
                                        <Tooltip
                                          title={
                                            titleActiveRightData.active
                                              ? t("tooltip:title.activeRights")
                                              : t("tooltip:title.noRights")
                                          }
                                          placement="top"
                                        >
                                          <Avatar
                                            sx={{
                                              color: titleActiveRightData.active ? "success.main" : "error.main",
                                              backgroundColor: titleActiveRightData.active
                                                ? "success.alpha"
                                                : "error.alpha",
                                              height: 24,
                                              width: 24,
                                            }}
                                          >
                                            <VerifiedOutlined fontSize="small" />
                                          </Avatar>
                                        </Tooltip>
                                      )}
                                    {titleContentData && titleContentData.hasConfidentialityInfos && (
                                      <Tooltip title={t("common:confidentiality")} placement="top">
                                        <Avatar
                                          sx={{
                                            color: "info.main",
                                            backgroundColor: "info.alpha",
                                            height: 24,
                                            width: 24,
                                          }}
                                        >
                                          <LockOutlined fontSize="small" />
                                        </Avatar>
                                      </Tooltip>
                                    )}
                                    {title!.isDeleted && (
                                      <Chip
                                        icon={<Warning />}
                                        label={t("common:deleted")}
                                        color="error"
                                        variant="outlined"
                                      />
                                    )}
                                  </Stack>
                                </Stack>

                                <Typography component="h1" variant="h2">
                                  {title?.name}
                                </Typography>
                              </Stack>
                              <Stack direction="row" gap={1} alignItems={"center"} mb={2}>
                                <Stack direction="row" gap={0.5}>
                                  {titleContentData && titleContentData.metadataList.titleType && (
                                    <Chip
                                      size="medium"
                                      variant="outlined"
                                      label={titleContentData.metadataList.titleType.value}
                                    />
                                  )}
                                  {titleContentData && titleContentData.metadataList.duration && (
                                    <Chip
                                      size="medium"
                                      variant="outlined"
                                      label={titleContentData.metadataList.duration.value + " min"}
                                    />
                                  )}
                                  {productionDate && (
                                    <Chip
                                      size="medium"
                                      variant="outlined"
                                      label={moment(productionDate).format("YYYY")}
                                    />
                                  )}
                                </Stack>
                                <Box
                                  sx={(t) => ({
                                    width: t.spacing(0.5),
                                    height: t.spacing(0.5),
                                    background: t.palette.divider,
                                  })}
                                />
                                <Stack direction="row" gap={0.5}>
                                  {titleContentData &&
                                    titleContentData.metadataList.allVideoQualities?.value.map((chip: any) => (
                                      <AssetVideoResolutionChip
                                        key={chip}
                                        label={chip}
                                        resolution={chip}
                                        size="small"
                                      />
                                    ))}
                                </Stack>
                              </Stack>
                            </Stack>
                            {title!.relatedObjects && title!.relatedObjects.length > 0 && (
                              <Select
                                IconComponent={ExpandMoreRoundedIcon}
                                size="small"
                                displayEmpty={true}
                                value=""
                                sx={{ maxWidth: 150 }}
                                inputProps={{ "aria-label": "Without label" }}
                                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                              >
                                <MenuItem value="">
                                  <Typography component="span" color="text.tier">
                                    {t("menu:item.linkedTitle", { count: title!.relatedObjects.length })}
                                  </Typography>
                                </MenuItem>

                                {title!.relatedObjects.map((value) => (
                                  <MenuItem
                                    key={value.id}
                                    value={value.name}
                                    onClick={() => navigate(GetTitleRouterPath() + "/" + value.id)}
                                  >
                                    {value.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          </Stack>
                        )}
                      </Stack>
                    </Stack>
                    <Stack direction="row" justifyContent="flex-end" alignItems="stretch" gap={1}>
                      <TopbarActions titleId={titleId} />
                    </Stack>
                  </Stack>
                  <TitleTabbar titleId={titleId} />
                </Stack>
              </Fade>
            </Collapse>
            <Slide in={shouldCollapse} direction="up" container={headingContainer.current} mountOnEnter unmountOnExit>
              <Stack
                direction="row"
                justifySelf="stretch"
                justifyContent={"space-between"}
                sx={{
                  alignItems: "center",
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: 0,
                  zIndex: 2,
                  opacity: shouldCollapse ? 1 : 0,
                  px: 2,
                }}
              >
                <Stack direction="row" alignItems={"center"} sx={{ flexShrink: 1, minWidth: 0 }}>
                  {/* <IconButton onClick={() => handleCollapsedHeadClick} size="small">
                    <ChevronRightOutlined />
                  </IconButton> */}
                  <TitleTabbar titleId={titleId} />
                </Stack>
                <Box
                  onClick={(e) => handleCollapsedHeadClick(e)}
                  onMouseEnter={() => setCollapsedHeadHover(true)}
                  onMouseLeave={() => setCollapsedHeadHover(false)}
                  sx={{ flexGrow: 1, height: GlobalConst.HEADING_HEIGHT_COLLAPSED, cursor: "pointer" }}
                />
                <Stack direction="row" gap={1}>
                  <TopbarActions minified titleId={titleId} />
                </Stack>
              </Stack>
            </Slide>
          </Box>
        </Fade>
      )}
    </Box>
  );
};

const TopbarActions: React.FC<{ titleId?: number; minified?: boolean }> = ({ titleId, minified = false }) => {
  const { t } = useTranslation();
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const title = useSelector(GetObject);
  const { addObjectToBasket } = useBasketActions();
  const { AddItemsToCollection } = useCollectionActions();
  const isLoading = useSelector(IsObjectLoading);
  const enqueueCustomSnackbar = useCustomSnackbar();
  const navigate = useNavigate();

  const [anchorMoreEl, setAnchorMoreEl] = useState<null | HTMLElement>(null);
  const moreOpen = Boolean(anchorMoreEl);
  const handleMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorMoreEl(event.currentTarget);
  };
  const handleMoreClose = () => {
    setAnchorMoreEl(null);
  };

  const [anchorMetadataEl, setAnchorMetadataEl] = useState<null | HTMLElement>(null);
  const metadataOpen = Boolean(anchorMetadataEl);
  const handleMetadataClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorMetadataEl(event.currentTarget);
  };
  const handleMetadataClose = () => {
    setAnchorMetadataEl(null);
  };

  const userHasRightToDelete = useMemo(
    () => UserHasAcl(AclTypes.aclMetadataTitleDelete) && IsPermissionGranted(title, PermissionType.Trash),
    [title]
  );

  const userCanCreateShare = useMemo(() => {
    return (
      UserHasAcl(AclTypes.aclShareCreate) &&
      (!title?.confidentialityInfos ||
        (title?.confidentialityInfos && UserHasAcl(AclTypes.aclTitleConfidentialityShares))) &&
      IsPermissionGranted(title, PermissionType.Share) &&
      UserHasLicence(Licences.ShareScreening)
    );
  }, [title]);

  const titleIsDeletable = useMemo(() => {
    return (
      userHasRightToDelete &&
      !title?.isDeleted &&
      title!.childObjects?.filter((x) => x.classification !== "Drive" && x.status !== "Deleted").length === 0 &&
      title!.childObjects?.filter((x) => x.classification === "Drive").map((drive) => drive.childObjects?.length === 0)
    );
  }, [userHasRightToDelete, title]);

  const openModalMetadata = () => {
    if (!openModal || !title) return;
    openModal<ModalEditMetadataElementProps>(ModalEditMetadata, { objects: [title] });
  };

  const openNewShareModal = useCallback(
    (object: DbAssetObjectInfos) => {
      if (!openModal) return;
      openModal(ModalCreateShare, { object: object });
    },
    [openModal]
  );

  const handleDeleteTitle = () => {
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: true,
      commentLabel: t("modal:comment.confirmDeleteTitle"),
      title: t("modal:title.deleteThisTitle"),
      onConfirm: () => handleDeleteTitleConfirm(),
    });
  };

  const handleDeleteTitleConfirm = () => {
    assetService.DeleteObjectById(title!.id).then((response) => {
      if (response.success) {
        navigate(routePaths.inventory.path);
        enqueueCustomSnackbar(t("alert:success.titleDeleted"), "success");
      } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");
    });
  };

  const handleOpenBundleMaker = () => {
    title && navigate(`${routePaths.title.path}/${title.id}/bundlemaker`);
  };

  return (
    <>
      {isLoading ? (
        <></>
      ) : (
        <>
          <Button
            startIcon={<ShoppingBagOutlinedIcon />}
            onClick={() => addObjectToBasket(title!)}
            disabled={!UserHasAcl(AclTypes.aclSectionOrderAccess)}
          >
            {t("common:order")}
          </Button>

          <Tooltip title={t("common:share")} placement="bottom">
            <IconButton
              size="small"
              color="neutral"
              onClick={() => openNewShareModal(title!)}
              disabled={!userCanCreateShare}
            >
              <ShareOutlinedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={t("button:addToCollection_one")} placement="bottom">
            <IconButton
              aria-controls={moreOpen ? "basic-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={moreOpen ? "true" : undefined}
              onClick={() => {
                if (title !== undefined) AddItemsToCollection([title]);
                return;
              }}
              size="small"
              color="neutral"
            >
              <BookmarkBorderIcon />
            </IconButton>
          </Tooltip>

          <IconButton
            aria-controls={moreOpen ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={moreOpen ? "true" : undefined}
            onClick={handleMoreClick}
            size="small"
            color="neutral"
          >
            <MoreHoriz />
          </IconButton>

          <Menu
            id="basic-menu"
            anchorEl={anchorMoreEl}
            open={moreOpen}
            onClose={handleMoreClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            {UserHasAcl(AclTypes.aclSectionOrderAccess) && <ToolsMenuItems disabled={false} title={title} />}

            {titleIsDeletable && (
              <MenuItem onClick={handleDeleteTitle}>
                <ListItemText sx={{ color: "error.main" }}>{t("menu:item.deleteTitle")}</ListItemText>
              </MenuItem>
            )}
          </Menu>

          {!minified && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
            </>
          )}

          <Button
            onClick={handleMetadataClick}
            startIcon={<Inventory2Outlined />}
            endIcon={<ExpandMoreRoundedIcon />}
            color="primary"
            variant="contained"
            aria-controls={moreOpen ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={moreOpen ? "true" : undefined}
          >
            {t("common:metadata")}
          </Button>
          <Menu anchorEl={anchorMetadataEl} open={metadataOpen} onClose={handleMetadataClose}>
            {UserHasAcls(
              [
                AclTypes.aclMetadataTitleUpdate,
                AclTypes.aclConnectLinkTitle,
                AclTypes.aclConnectImportTitle,
                AclTypes.aclSyncTitleFromSpock,
              ],
              AclMatch.OneAtLeast
            ) && (
              <MenuItem onClick={openModalMetadata}>
                <ListItemIcon>
                  <EditOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                </ListItemIcon>
                <ListItemText>{t("common:editMetadataUpperCase")}</ListItemText>
              </MenuItem>
            )}
            {title && UserHasAcl(AclTypes.aclAssetBundlemaker) && UserHasLicence(Licences.BundleMakerMetadata) && (
              <MenuItem onClick={() => navigate(`${routePaths.title.path}/${title.id}/bundlemaker`)}>
                <ListItemIcon>
                  <FolderZipOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
                </ListItemIcon>
                <ListItemText>{t("menu:item.openBundlemaker")}</ListItemText>
              </MenuItem>
            )}
            <ExportMenuItems titleId={titleId} />
          </Menu>
        </>
      )}
    </>
  );
};

const TitleTabbar: FC<{ titleId: number; minified?: boolean }> = ({ titleId, minified }) => {
  const { t } = useTranslation();

  return (
    <Tabbar
      sx={(theme) =>
        minified
          ? {
              height: "100%",
              ".MuiTabs-flexContainer": {
                height: "100%",
              },
            }
          : {}
      }
      tabs={[
        [
          {
            label: t("page:tabbar.label.contentsAndMetadata"),
            to: `${routePaths.title.path}/${titleId}`,
            hide: UserHasAcl(AclTypes.aclDistributionDriveOnly),
          },
          {
            label: t("common:assets"),
            to: `${routePaths.title.path}/${titleId}/${routePaths.titleAssets.path}`,
            hide:
              cacheService.GetGroup()?.aclAssetAccessLevel === AclAssetAccessLevel.Title ||
              UserHasAcl(AclTypes.aclDistributionDriveOnly),
          },
          {
            label: t("page:tabbar.label.drive"),
            to: `${routePaths.title.path}/${titleId}/${routePaths.titleDrive.path}`,
            hide: !(UserHasLicence(Licences.Drive) && UserHasAcl(AclTypes.aclDrive)),
            matchingPaths: [`${routePaths.title.path}/${titleId}/${routePaths.titleDrive.path}/:childId`],
          },
          {
            label: t("page:tabbar.label.mediaAndCollections"),
            to: `${routePaths.title.path}/${titleId}/${routePaths.titleMediaAndCollections.path}`,
            hide: UserHasAcl(AclTypes.aclDistributionDriveOnly),
          },
          {
            label: t("route:pageTitle.contracts"),
            to: `${routePaths.title.path}/${titleId}/${routePaths.titleContracts.path}`,
            hide: !UserHasAcl(AclTypes.aclRightsView) || UserHasAcl(AclTypes.aclDistributionDriveOnly),
          },
          {
            label: t("common:orders"),
            to: `${routePaths.title.path}/${titleId}/${routePaths.titleOrders.path}`,
            hide: !UserHasAcl(AclTypes.aclSectionOrderAccess) || UserHasAcl(AclTypes.aclDistributionDriveOnly),
          },
          {
            label: t("common:shares"),
            to: `${routePaths.title.path}/${titleId}/${routePaths.titleShares.path}`,
            hide: !UserHasAcl(AclTypes.aclShareView) || UserHasAcl(AclTypes.aclDistributionDriveOnly),
          },
          {
            label: (
              <>
                {t("common:activities")} {/*<RequiredIndicator sx={{ ml: 0.5 }} />*/}
              </>
            ),
            to: `${routePaths.title.path}/${titleId}/${routePaths.titleActivities.path}`,
            hide: UserHasAcl(AclTypes.aclDistributionDriveOnly),
          },
        ],
      ]}
    />
  );
};

const ExportMenuItems: FC<{ titleId?: number; minified?: boolean }> = ({ titleId, minified }) => {
  const { t } = useTranslation();
  const enqueueCustomSnackbar = useCustomSnackbar();

  const onClickExport = () => {
    if (titleId)
      assetService.DownloadTitleAsExcelFile(titleId).then(function (response) {
        if (response.success) enqueueCustomSnackbar(t("alert:success.exportWillBeSentByEmail"), "success");
        else enqueueCustomSnackbar(t("alert:error.exportFailed"), "error");
      });
  };

  const onClickDownloadMetadata = (includeChildren: boolean) => {
    if (titleId)
      assetService.DownloadTitleMetadata(titleId, includeChildren).then(function (response) {
        if (response) enqueueCustomSnackbar(t("alert:success.downdloadSuccessful"), "success");
        else enqueueCustomSnackbar(t("alert:error.downloadFailedWithDot"), "error");
      });
  };

  return (
    <>
      <ListSubheader sx={{ py: 1, px: 2 }}>
        <Typography variant="body1">{t("component:body.exportMetadata")}</Typography>
      </ListSubheader>
      <MenuItem
        onClick={() => onClickDownloadMetadata(true)}
        disabled={!titleId || !UserHasAcl(AclTypes.aclMetadataExport)}
      >
        <ListItemIcon sx={{ fontSize: 16, color: "text.secondary" }}>
          <SaveAltOutlined />
        </ListItemIcon>
        <ListItemText>{t("menu:item.downloadAllMetadata")}</ListItemText>
      </MenuItem>
      <MenuItem
        onClick={() => onClickDownloadMetadata(false)}
        disabled={!titleId || !UserHasAcl(AclTypes.aclMetadataExport)}
      >
        <ListItemIcon sx={{ fontSize: 16, color: "text.secondary" }}>
          <SaveAltOutlined />
        </ListItemIcon>
        <ListItemText>{t("menu:item.downloadObjectMetadata")}</ListItemText>
      </MenuItem>
      <MenuItem onClick={() => onClickExport()} disabled={!titleId || !UserHasAcl(AclTypes.aclAssetPdfExport)}>
        <ListItemIcon sx={{ fontSize: 16, color: "text.secondary" }}>
          <SaveAltOutlined />
        </ListItemIcon>
        <ListItemText>{t("menu:item.exportAsExcel")}</ListItemText>
      </MenuItem>
    </>
  );
};

const ToolsMenuItems: FC<{
  disabled: boolean | undefined;
  title: DbAssetObjectInfos | undefined;
  minified?: boolean;
}> = ({ disabled, title, minified }) => {
  const { t } = useTranslation();
  const [anchorMoreEl, setAnchorMoreEl] = useState<null | HTMLElement>(null);

  enum ToolType {
    AddOVDCP,
    AddReels,
  }

  const [openOV, setOpenOV] = useState(false);
  const [openReels, setOpenReels] = useState(false);
  const { addPreconfiguredItemDCPReelsToBasket, addPreconfiguredItemDCPOVToBasket } = useBasketActions();
  const enqueueCustomSnackbar = useCustomSnackbar();

  const handleMoreClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorMoreEl(e.currentTarget);
  };

  const handleMoreClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorMoreEl(null);
  };

  const handleAddTobasket = useCallback((type: ToolType, id: number) => {
    switch (type) {
      case ToolType.AddOVDCP:
        addPreconfiguredItemDCPOVToBasket(id ?? -1);
        setOpenOV(false);
        break;
      case ToolType.AddReels:
        addPreconfiguredItemDCPReelsToBasket(id ?? -1);
        setOpenReels(false);
        break;
    }

    setAnchorMoreEl(null);
  }, []);

  const handleThumbClearCache = () => {
    if (!title) return;

    thumbnailService.CleanCacheThumbRefByObjectId(title.id).then((response) => {
      if (response.success) enqueueCustomSnackbar(t("alert:primary.thumbnailCacheIsCleared"), "primary");
    });
  };

  const openMore = Boolean(anchorMoreEl);

  const _renderTooltip = (type: ToolType) => {
    return (
      <Stack sx={{ minWidth: 200 }} pt={0.5} pb={0.5}>
        <Select
          value={""}
          displayEmpty
          inputProps={{ "aria-label": "Without label" }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          onChange={(value) => handleAddTobasket(type, parseInt(value.target.value))}
          fullWidth
        >
          <MenuItem value={""} key={""}>
            {t("common:selectSourceContent")}
          </MenuItem>
          {title?.childObjects?.map((content) => (
            <MenuItem value={content.id} key={content.id}>
              {content.name}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    );
  };

  return (
    <>
      <Tooltip
        PopperProps={{
          disablePortal: false,
        }}
        onClose={() => setOpenOV(false)}
        open={openOV}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        title={_renderTooltip(ToolType.AddOVDCP)}
        placement="right-end"
        arrow
      >
        <MenuItem
          onClick={() => {
            setOpenOV((prev) => !prev);
          }}
        >
          {t("menu:item.addDcpOVToBasket")}
        </MenuItem>
      </Tooltip>

      <Tooltip
        PopperProps={{
          disablePortal: false,
        }}
        onClose={() => setOpenReels(false)}
        open={openReels}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        title={_renderTooltip(ToolType.AddReels)}
        placement="right-end"
        arrow
      >
        <MenuItem
          onClick={() => {
            setOpenReels((prev) => !prev);
          }}
        >
          {t("menu:item.addDcpReelsToBasket")}
        </MenuItem>
      </Tooltip>
      {UserHasAcls([AclTypes.aclSuperUser, AclTypes.aclThumbnailClearCache], AclMatch.OneAtLeast) && (
        <MenuItem
          onClick={() => {
            handleThumbClearCache();
          }}
        >
          {t("menu:item.clearThumbnailCache")}
        </MenuItem>
      )}
    </>
  );
};

const TitleHeadingSkeleton = () => {
  const theme = useTheme();
  return (
    <Stack gap={1} height={270} pt={2}>
      <Stack direction="row" spacing={3} flexBasis="50%">
        <Skeleton width={125} variant="rectangular" height={190} sx={{ borderRadius: 1 }} />
        <Stack alignItems="flex-start">
          <Stack gap={1} flex={1}>
            <Skeleton width={100} height={20} />
            <Skeleton width={200} height={40} />
            <Skeleton width={300} height={20} />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default TitleHeading;
